import { createClient } from "graphql-ws";
import { ApolloClient, ApolloLink, split, InMemoryCache } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { TokenRefreshLink } from "apollo-link-token-refresh";
import { createUploadLink } from "apollo-upload-client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import {
  localStorage,
  isAccessTokenExpired,
  fetchAccessToken,
  fileUploadFetch,
} from "dash-ui";

import { TokensModel } from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";

const GRAPHQL_URI = process.env["REACT_APP_GRAPHQL"] as string;

const httpLink = createUploadLink({
  uri: GRAPHQL_URI,
  fetch: (uri: any, options: any): any =>
    options.useUpload ? fileUploadFetch(uri, options) : fetch(uri, options),
}) as unknown as ApolloLink;

const authLink = setContext((_, { headers }) => {
  // Get the access token from local storage if it exists
  const accessToken = localStorage.getAccessToken();
  // Return the headers to the context so batchHttpLink can read them
  return {
    headers: {
      authorization: accessToken ? `Bearer ${accessToken}` : "",
      ...headers,
    },
  };
});

const appKeyLink = setContext((_, context) => {
  return {
    headers: { app_key: process.env["REACT_APP_KEY"], ...context.headers },
  };
});

// Store the pending refresh promise to prevent duplicate requests
let pendingTokenRefresh: Promise<Response> | null = null;

const refreshTokenLink = new TokenRefreshLink<string>({
  isTokenValidOrUndefined: () => {
    const accessToken = localStorage.getAccessToken() || "";
    const refreshToken = localStorage.getRefreshToken() || "";

    // If there's no refresh token, we're in MFA flow or not authenticated
    // Don't attempt to refresh - let the request proceed with current token
    if (!refreshToken) {
      return true;
    }

    return !isAccessTokenExpired(accessToken);
  },
  fetchAccessToken: (): Promise<Response> => {
    const refreshToken = localStorage.getRefreshToken() || "";

    // Don't attempt refresh if no refresh token exists
    if (!refreshToken) {
      return Promise.reject(new Error("No refresh token available"));
    }

    // If a refresh is already in progress, return the pending promise
    if (pendingTokenRefresh) {
      return pendingTokenRefresh;
    }

    // Clear the access token and create a new refresh promise
    localStorage.setAccessToken("");
    window.dispatchEvent(new Event("storage"));

    // Store the promise to prevent duplicate requests
    pendingTokenRefresh = fetchAccessToken(GRAPHQL_URI, refreshToken).finally(
      () => {
        // Clear the pending promise when done (success or failure)
        pendingTokenRefresh = null;
      }
    );

    return pendingTokenRefresh;
  },
  handleFetch: (accessToken: string) => {
    localStorage.setAccessToken(accessToken);
    window.dispatchEvent(new Event("storage"));
  },
  handleResponse: () => (refreshTokens: TokensModel) => ({
    access_token: refreshTokens.accessToken,
  }),
  handleError: (error: any) => {
    const refreshToken = localStorage.getRefreshToken() || "";

    // Only clear auth and redirect if we actually have a refresh token
    // (i.e., we're in a normal authenticated session, not MFA flow)
    if (refreshToken) {
      // Check if this is an authentication error that requires logout
      const isAuthError = error?.message === "Unauthorized";

      if (isAuthError) {
        // Clear all auth state when refresh token is invalid
        localStorage.setAccessToken("");
        localStorage.setRefreshToken("");
        localStorage.setRefreshTokenExpiry("");

        // Dispatch storage event to notify other components
        window.dispatchEvent(new Event("storage"));

        // Redirect to authentication sign-in page
        window.location.href = RoutesPath.SignIn;
      } else {
        // For other errors (network, etc), just log but don't logout
        console.error(`Token refresh error: ${error?.message}`);
        // The request will fail, but user stays logged in
      }
    }
  },
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${GRAPHQL_URI.replace(/^http?/, "ws")}/subscriptions`,
    connectionParams: () => {
      const accessToken = localStorage.getAccessToken();

      return {
        authorization: accessToken ? `Bearer ${accessToken}` : "",
        app_key: process.env["REACT_APP_KEY"],
      };
    },
  })
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  ApolloLink.from([appKeyLink, refreshTokenLink, authLink, httpLink])
);

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: splitLink,
});
